import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

/**
 * Automatic Cycle Tagging Utilities
 * Handles automatic assignment of CEUs to cycles based on workshop dates
 */

/**
 * Generate cycle ID from start and end dates
 * @param {string} startDate - Cycle start date (YYYY-MM-DD)
 * @param {string} endDate - Cycle end date (YYYY-MM-DD)
 * @returns {string} Cycle ID (e.g., "cycle_2025_2028")
 */
export function generateCycleId(startDate, endDate) {
  const startYear = dayjs(startDate).year();
  const endYear = dayjs(endDate).year();
  return `cycle_${startYear}_${endYear}`;
}

/**
 * Generate human-readable cycle period
 * @param {string} startDate - Cycle start date
 * @param {string} endDate - Cycle end date
 * @returns {string} Cycle period (e.g., "2025-2028")
 */
export function generateCyclePeriod(startDate, endDate) {
  const startYear = dayjs(startDate).year();
  const endYear = dayjs(endDate).year();
  return `${startYear}-${endYear}`;
}

/**
 * Automatically tag CEU with cycle information based on workshop date
 * @param {Object} ceu - CEU object with workshop date
 * @param {Object} userProfile - User profile with cycle information
 * @returns {Object} CEU with cycle fields populated
 */
export function autoTagCycleFields(ceu, userProfile) {
  if (!ceu.date || !userProfile) {
    return ceu;
  }

  // Get cycle dates first
  const workshopDate = dayjs(ceu.date);
  const cycleStart = userProfile.currentCycleStart || userProfile.cycleStartDate;
  const cycleEnd = userProfile.currentCycleEnd || userProfile.cycleEndDate;

  // Always recalculate cycle assignment based on workshop date vs current cycle dates
  if (cycleStart && cycleEnd) {
    const currentStart = dayjs(cycleStart);
    const currentEnd = dayjs(cycleEnd);
    
    if (workshopDate.isBetween(currentStart, currentEnd, 'day', '[]')) {
      return {
        ...ceu,
        isCurrentCycle: true,
        cycleStartDate: cycleStart,
        cycleEndDate: cycleEnd,
        cyclePeriod: generateCyclePeriod(cycleStart, cycleEnd)
      };
    }
  }

  // If not in current cycle, check if it's in a previous cycle
  // For now, mark as previous cycle (false) - could be enhanced to detect specific previous cycles
  return {
    ...ceu,
    isCurrentCycle: false,
    cyclePeriod: ceu.cyclePeriod || 'Previous Cycle'
  };
}

/**
 * Batch tag multiple CEUs with cycle information
 * @param {Array} ceus - Array of CEU objects
 * @param {Object} userProfile - User profile with cycle information
 * @returns {Array} CEUs with cycle fields populated
 */
export function batchTagCycleFields(ceus, userProfile) {
  const taggedCEUs = ceus.map(ceu => autoTagCycleFields(ceu, userProfile));
  
  // Log summary instead of individual warnings
  if (process.env.NODE_ENV === 'development') {
    // Development debugging available if needed
  }
  
  return taggedCEUs;
}

/**
 * Filter CEUs by cycle
 * @param {Array} ceus - Array of CEU objects
 * @param {string} cycleId - Cycle ID to filter by
 * @returns {Array} Filtered CEUs
 */
export function filterCEUsByCycle(ceus, cycleId) {
  return ceus.filter(ceu => ceu.cycleId === cycleId);
}

/**
 * Get current cycle CEUs
 * @param {Array} ceus - Array of CEU objects
 * @returns {Array} Current cycle CEUs only
 */
export function getCurrentCycleCEUs(ceus) {
  return ceus.filter(ceu => ceu.isCurrentCycle === true);
}

/**
 * Get previous cycle CEUs
 * @param {Array} ceus - Array of CEU objects
 * @returns {Array} Previous cycle CEUs only
 */
export function getPreviousCycleCEUs(ceus) {
  return ceus.filter(ceu => ceu.isCurrentCycle === false);
}

/**
 * Group CEUs by cycle period
 * @param {Array} ceus - Array of CEU objects
 * @returns {Object} CEUs grouped by cycle period
 */
export function groupCEUsByCycle(ceus) {
  const grouped = {};
  
  ceus.forEach(ceu => {
    const cyclePeriod = ceu.cyclePeriod || 'Unknown';
    if (!grouped[cyclePeriod]) {
      grouped[cyclePeriod] = [];
    }
    grouped[cyclePeriod].push(ceu);
  });
  
  return grouped;
}

/**
 * Get cycle summary statistics
 * @param {Array} ceus - CEUs for a specific cycle
 * @param {Object} userProfile - User profile for requirements
 * @returns {Object} Cycle summary with totals and progress
 */
export function getCycleSummary(ceus, userProfile) {
  const totalCEUs = ceus.reduce((sum, ceu) => sum + (ceu.ceuHours || 0), 0);
  
  const categories = {
    'Professional Studies': 0,
    'General Studies': 0,
    'PPO': 0
  };
  
  ceus.forEach(ceu => {
    if (ceu.category && categories.hasOwnProperty(ceu.category)) {
      categories[ceu.category] += (ceu.ceuHours || 0);
    }
    
    // Special handling for PPO
    if (ceu.subCategory === 'PPO' || (Array.isArray(ceu.subCategory) && ceu.subCategory.includes('PPO'))) {
      categories['PPO'] += (ceu.ceuHours || 0);
    }
  });
  
  return {
    totalCEUs: totalCEUs.toFixed(1),
    professionalStudies: categories['Professional Studies'].toFixed(1),
    generalStudies: categories['General Studies'].toFixed(1),
    ppo: categories['PPO'].toFixed(1),
    ceuCount: ceus.length,
    lastUpdated: dayjs().format('YYYY-MM-DD')
  };
}

const cycleTaggingUtils = {
  generateCycleId,
  generateCyclePeriod,
  autoTagCycleFields,
  batchTagCycleFields,
  filterCEUsByCycle,
  getCurrentCycleCEUs,
  getPreviousCycleCEUs,
  groupCEUsByCycle,
  getCycleSummary
};

export default cycleTaggingUtils;
